import { useMemo } from "react";
import { Link, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Dumbbell,
  Figure,
  Ledger,
  RankedBars,
  SlopeChart,
  StackedBars,
  STACK_GRAYS,
  seriesColors,
  type StackKey,
} from "~/components/charts";
import { api } from "~/lib/api";
import { usePeriod } from "~/lib/period";
import { RosterTable } from "./components/RosterTable";
import { Masthead } from "./components/Masthead";
import { biggestMovers, buildPageModel, pointsText } from "./pageModel";
import { buildDek, buildHeadline, firstName, isConcentrated } from "./story";
import type { DevelopersResponse, RosterPerson } from "./types";
import { changeText, countWord, fmtHours, fmtNum, inPeriod, periodName, priorName } from "~/lib/format";
import { ed } from "~/components/editorial";

const FIGURE_ROWS = 24;

export function DevelopersPage() {
  const [searchParams] = useSearchParams();
  const { range, window } = usePeriod();
  const teamId = searchParams.get("teamId") || "";

  const qs = new URLSearchParams({ range });
  if (teamId) qs.set("teamId", teamId);

  const { data, isLoading, error } = useQuery({
    queryKey: ["developers", { range, teamId }],
    queryFn: () => api.get<DevelopersResponse>(`/developers?${qs.toString()}`),
    staleTime: 30 * 1000,
  });

  const items = useMemo(() => data?.data.items ?? [], [data]);
  const period = data?.data.period ?? {
    label: window.label,
    prevLabel: window.prevLabel,
    asOf: new Date().toISOString(),
  };
  const team = data?.data.team;
  const model = useMemo(
    () => buildPageModel(items, period, team),
    // period labels are stable for a given response
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, team, period.label, period.prevLabel],
  );

  const now = periodName(period.label);
  const prior = priorName(period.prevLabel);
  const teamName = teamId
    ? (items.find((p) => p.teams.some((t) => t.id === teamId))?.teams.find((t) => t.id === teamId)
        ?.name ?? "One team")
    : "All engineering";

  return (
    <div className={ed.page}>
      <div className={ed.inner}>
        <Masthead periodLabel={period.label} prevLabel={period.prevLabel} asOf={period.asOf} />

        {isLoading ? (
          <div className={ed.loading}>Loading the roster…</div>
        ) : error ? (
          <div className={ed.loading}>Couldn't load developer stats: {String((error as Error).message)}</div>
        ) : (
          <>
            <div className={ed.kicker}>
              {teamName} · {now}
            </div>
            <h1 className={ed.headline}>{buildHeadline(model.story)}</h1>
            <p className={ed.dek}>{buildDek(model.story)}</p>
            <div className={ed.byline}>
              <b>Developers</b> · <span className="num">{fmtNum(model.active.length)}</span> people with
              engineering work {inPeriod(period.label)},
              compared with {prior}. From GitHub and JIRA.
            </div>

            <Ledger
              items={[
                {
                  label: "PRs merged",
                  current: team?.current.prsMerged ?? null,
                  prior: team?.previous.prsMerged ?? null,
                  ...changeText(team?.current.prsMerged ?? null, team?.previous.prsMerged ?? null),
                },
                {
                  label: "People shipping code",
                  current: model.story.activePeople,
                  prior: model.story.prevActivePeople,
                  ...changeText(model.story.activePeople, model.story.prevActivePeople),
                },
                {
                  label: "Open to merge",
                  current: team?.current.medianHoursToMerge ?? null,
                  prior: team?.previous.medianHoursToMerge ?? null,
                  format: fmtHours,
                  note: "median",
                  ...changeText(
                    team?.current.medianHoursToMerge ?? null,
                    team?.previous.medianHoursToMerge ?? null,
                    { minBaseForPct: 0 },
                  ),
                },
                {
                  label: "Wait for review",
                  current: team?.current.medianHoursToFirstReview ?? null,
                  prior: team?.previous.medianHoursToFirstReview ?? null,
                  format: fmtHours,
                  note: "median, first human review",
                  ...changeText(
                    team?.current.medianHoursToFirstReview ?? null,
                    team?.previous.medianHoursToFirstReview ?? null,
                    { minBaseForPct: 0 },
                  ),
                },
                {
                  label: "Reviewed first",
                  current: team?.current.reviewedBeforeMergeShare ?? null,
                  prior: team?.previous.reviewedBeforeMergeShare ?? null,
                  format: (v) => (v === null ? "–" : `${v}%`),
                  note: "of merged PRs",
                  flag:
                    (team?.current.reviewedBeforeMergeShare ?? 100) < 80 ? "caution" : undefined,
                  ...pointChange(
                    team?.current.reviewedBeforeMergeShare ?? null,
                    team?.previous.reviewedBeforeMergeShare ?? null,
                  ),
                },
                {
                  label: "Tickets done",
                  current: team?.current.ticketsDone ?? null,
                  prior: team?.previous.ticketsDone ?? null,
                  note: team ? `${pointsText(team.current.pointsDone)} pts` : undefined,
                  ...changeText(team?.current.ticketsDone ?? null, team?.previous.ticketsDone ?? null),
                },
              ]}
            />

            <ReviewSection model={model} prior={prior} now={now} />
            <ThroughputSection people={model.active} quiet={model.wentQuiet} prior={prior} now={now} team={team} />
            <WorkMixSection people={model.active} />

            <div className={ed.sect}>V. The roster</div>
            <h2 className={ed.h2}>Everyone, this period against {prior}</h2>
            <p className={ed.prose}>
              All <span className="num">{fmtNum(model.active.length)}</span> people with code or
              ticket work in {now}. Small figures beside each number are the change from the same
              days of {prior}; the bars show this period in black and {prior} as a tick.
              {model.unlinkedCount > 0 && (
                <>
                  {" "}
                  <span className="num">{fmtNum(model.unlinkedCount)}</span>{" "}
                  {model.unlinkedCount === 1 ? "profile isn't" : "profiles aren't"} linked to a
                  GitHub account and may duplicate someone;{" "}
                  <Link to="/settings/developers">merge them in Developer Identities</Link>.
                </>
              )}
            </p>
            <RosterTable people={model.active} quiet={model.wentQuiet} priorName={prior} />

            <Definitions />
          </>
        )}
      </div>
    </div>
  );
}

function pointChange(current: number | null, prior: number | null) {
  if (current === null || prior === null) return {};
  const d = current - prior;
  return {
    change: d === 0 ? "no change" : `${d > 0 ? "+" : ""}${d} pts`,
    arrow: d > 0 ? "▲" : d < 0 ? "▼" : "",
  };
}

function ReviewSection({
  model,
  prior,
  now,
}: {
  model: ReturnType<typeof buildPageModel>;
  prior: string;
  now: string;
}) {
  const c = model.concentration;
  const reviewers = model.active
    .filter((p) => p.reviewsGiven > 0 || (p.previous?.reviewsGiven ?? 0) > 0)
    .sort((a, b) => b.reviewsGiven - a.reviewsGiven);
  const top = new Set(c.topIds);
  const concentrated = isConcentrated(c);
  const shown = reviewers.slice(0, FIGURE_ROWS);
  const names = reviewers.slice(0, Math.min(c.topN, 3)).map((p) => firstName(p.displayName));
  const prevC = model.prevConcentration;

  return (
    <>
      <div className={ed.sect}>I. Review load</div>
      <h2 className={ed.h2}>
        {c.reviewers === 0
          ? `No reviews recorded in ${now}`
          : `${capital(countWord(c.topN))} of ${fmtNum(c.reviewers)} reviewers did half the reviewing`}
      </h2>
      {c.reviewers > 0 && (
        <p className={ed.prose}>
          {names.join(", ")}
          {c.topN > names.length ? " and others" : ""} reviewed the most PRs in {now}; the top{" "}
          <span className="num">{c.topN}</span> covered <span className="num">{c.topShare}%</span> of
          the <span className="num">{fmtNum(c.totalReviews)}</span> person-PR reviews.
          {prevC.reviewers > 0 && (
            <>
              {" "}
              Over {prior} it took <span className="num">{prevC.topN}</span> of{" "}
              <span className="num">{prevC.reviewers}</span> to reach half.
            </>
          )}
          {concentrated ? " That is a short bench if any of them is out." : ""}
        </p>
      )}
      {shown.length > 0 && (
        <Figure
          title={`PRs reviewed per person, ${now}`}
          sub={`Top ${c.topN} in color. Tick marks show ${prior}.`}
          method={`Distinct PRs by someone else that each person reviewed (approved, requested changes or commented), ${now} vs. ${prior}. Own PRs and bots excluded.${reviewers.length > shown.length ? ` Showing ${shown.length} of ${reviewers.length}; the table has all.` : ""}`}
          table={{
            columns: ["Person", now, prior, "Decided"],
            rows: reviewers.map((p) => [
              p.displayName,
              p.reviewsGiven,
              p.previous?.reviewsGiven ?? "–",
              p.decisiveReviews,
            ]),
          }}
        >
          <RankedBars
            ariaLabel="PRs reviewed per person"
            priorLabel={prior}
            rows={shown.map((p) => ({
              id: p.id,
              label: p.displayName,
              value: p.reviewsGiven,
              prior: p.previous?.reviewsGiven ?? null,
              emphasis: top.has(p.id),
            }))}
            cut={
              c.topN > 0 && c.topN < shown.length
                ? { after: c.topN, note: `Top ${c.topN}: ${c.topShare}% of all reviews` }
                : undefined
            }
          />
        </Figure>
      )}
    </>
  );
}

function ThroughputSection({
  people,
  quiet,
  prior,
  now,
  team,
}: {
  people: RosterPerson[];
  quiet: RosterPerson[];
  prior: string;
  now: string;
  team: DevelopersResponse["data"]["team"];
}) {
  const shippers = [...people, ...quiet]
    .filter((p) => p.prsMerged > 0 || (p.previous?.prsMerged ?? 0) > 0)
    .sort((a, b) => b.prsMerged - a.prsMerged || (b.previous?.prsMerged ?? 0) - (a.previous?.prsMerged ?? 0));
  const { gain, drop } = biggestMovers(shippers);
  // Always draw the two people the text names, even below the cut
  const top = shippers.slice(0, FIGURE_ROWS);
  const shown = [
    ...top,
    ...[gain, drop].filter(
      (p): p is RosterPerson => p !== null && !top.some((t) => t.id === p.id),
    ),
  ];
  const up = shippers.filter((p) => p.prsMerged > (p.previous?.prsMerged ?? 0)).length;
  const down = shippers.filter((p) => p.prsMerged < (p.previous?.prsMerged ?? 0)).length;

  return (
    <>
      <div className={ed.sect}>II. Throughput</div>
      <h2 className={ed.h2}>
        {fmtNum(up)} people merged more than in {prior}, {fmtNum(down)} merged less
      </h2>
      <p className={ed.prose}>
        {gain && (
          <>
            The largest gain was {gain.displayName}, from{" "}
            <span className="num">{gain.previous?.prsMerged ?? 0}</span> to{" "}
            <span className="num">{gain.prsMerged}</span>.{" "}
          </>
        )}
        {drop && (
          <>
            The largest drop was {drop.displayName}, from{" "}
            <span className="num">{drop.previous?.prsMerged ?? 0}</span> to{" "}
            <span className="num">{drop.prsMerged}</span>.{" "}
          </>
        )}
        {quiet.length > 0 && (
          <>
            <span className="num">{quiet.length}</span>{" "}
            {quiet.length === 1 ? "person" : "people"} with work in {prior} had none recorded in {now}
            {quiet.length <= 4 ? `: ${quiet.map((p) => p.displayName).join(", ")}` : ""}.
          </>
        )}
      </p>
      <div className={ed.twoUp}>
        {shown.length > 0 ? (
          <Figure
            title={`Merged PRs per person, ${prior} to ${now}`}
            sub="Hollow dot is last period, filled is this one."
            method={`PRs each person authored that merged in the window, by merge date. Same elapsed days in both periods.${shippers.length > shown.length ? ` Showing ${shown.length} of ${shippers.length}.` : ""}`}
            table={{
              columns: ["Person", prior, now, "Change"],
              rows: shippers.map((p) => {
                const was = p.previous?.prsMerged ?? 0;
                return [p.displayName, was, p.prsMerged, p.prsMerged - was];
              }),
            }}
          >
            <Dumbbell
              ariaLabel="Merged PRs per person, last period and this one"
              fromLabel={prior}
              toLabel={now}
              rows={shown.map((p) => ({
                id: p.id,
                label: p.displayName,
                from: p.previous?.prsMerged ?? 0,
                to: p.prsMerged,
                tone: p.id === gain?.id ? "emph" : p.id === drop?.id ? "caution" : undefined,
                annotation: p.id === gain?.id ? "largest gain" : p.id === drop?.id ? "largest drop" : undefined,
              }))}
            />
          </Figure>
        ) : (
          <p className={ed.prose}>No merged PRs in either period.</p>
        )}
        <Figure
          title="How long PRs waited"
          sub={`Team medians, ${prior} and ${now}`}
          method="Open to merge: median over PRs merged in the window. First review: median time from open to the first review by someone other than the author, bots excluded."
          table={{
            columns: ["Measure", prior, now],
            rows: [
              ["Open to merge", fmtHours(team?.previous.medianHoursToMerge), fmtHours(team?.current.medianHoursToMerge)],
              ["First review", fmtHours(team?.previous.medianHoursToFirstReview), fmtHours(team?.current.medianHoursToFirstReview)],
            ],
          }}
        >
          <SlopeChart
            ariaLabel="Median hours to first review and to merge"
            fromLabel={prior}
            toLabel={now}
            format={fmtHours}
            series={[
              {
                id: "merge",
                label: "To merge",
                from: team?.previous.medianHoursToMerge ?? null,
                to: team?.current.medianHoursToMerge ?? null,
                tone: slopeTone(team?.previous.medianHoursToMerge, team?.current.medianHoursToMerge),
              },
              {
                id: "review",
                label: "First review",
                from: team?.previous.medianHoursToFirstReview ?? null,
                to: team?.current.medianHoursToFirstReview ?? null,
                tone: slopeTone(team?.previous.medianHoursToFirstReview, team?.current.medianHoursToFirstReview),
              },
            ]}
          />
        </Figure>
      </div>
    </>
  );
}

/** Color the line only when it moved by a quarter or more. */
function slopeTone(
  from: number | null | undefined,
  to: number | null | undefined,
): "emph" | "caution" | undefined {
  if (from === null || from === undefined || to === null || to === undefined || from === 0)
    return undefined;
  const change = (to - from) / from;
  if (change >= 0.25) return "caution";
  if (change <= -0.25) return "emph";
  return undefined;
}

function WorkMixSection({ people }: { people: RosterPerson[] }) {
  const withMix = people
    .filter((p) => p.workCategories.length > 0)
    .sort((a, b) => sumCats(b) - sumCats(a));
  const totals = new Map<string, { label: string; count: number }>();
  for (const p of withMix) {
    for (const c of p.workCategories) {
      const t = totals.get(c.category) ?? { label: c.label, count: 0 };
      t.count += c.count;
      totals.set(c.category, t);
    }
  }
  const ordered = [...totals.entries()].sort((a, b) => b[1].count - a[1].count);
  const all = ordered.reduce((s, [, t]) => s + t.count, 0);
  // Color the two largest categories in series order; the rest stay gray.
  const keys: StackKey[] = ordered.map(([key, t], i) => ({
    key,
    label: t.label,
    color: i < 2 ? seriesColors[i]! : STACK_GRAYS[Math.min(i - 2, STACK_GRAYS.length - 1)]!,
  }));
  const shown = withMix.slice(0, FIGURE_ROWS);
  const lead = ordered[0];
  const second = ordered[1];

  return (
    <>
      <div className={ed.sect}>III. The work</div>
      <h2 className={ed.h2}>
        {lead
          ? `${lead[1].label} made up ${Math.round((lead[1].count / all) * 100)}% of merged PRs`
          : "No categorized merges this period"}
      </h2>
      {lead && (
        <p className={ed.prose}>
          Of <span className="num">{fmtNum(all)}</span> merged PRs,{" "}
          <span className="num">{fmtNum(lead[1].count)}</span> were {lead[1].label.toLowerCase()}
          {second && (
            <>
              {" "}
              and <span className="num">{fmtNum(second[1].count)}</span> were{" "}
              {second[1].label.toLowerCase()}
            </>
          )}
          . Each bar below is one person's merged PRs split by category.
        </p>
      )}
      {shown.length > 0 && (
        <Figure
          title="What each person's merged PRs were"
          sub={`Share of each person's merged PRs. The two largest categories in color.`}
          method={`Category from the linked JIRA issue type, else the branch and title (feature, fix, refactor, docs, release, infra). PRs merged in the window.${withMix.length > shown.length ? ` Showing ${shown.length} of ${withMix.length}.` : ""}`}
          table={{
            columns: ["Person", ...keys.map((k) => k.label)],
            rows: withMix.map((p) => [
              p.displayName,
              ...keys.map((k) => p.workCategories.find((c) => c.category === k.key)?.count ?? 0),
            ]),
          }}
        >
          <StackedBars
            ariaLabel="Merged PRs by category per person"
            keys={keys}
            rows={shown.map((p) => ({
              id: p.id,
              label: p.displayName,
              values: Object.fromEntries(p.workCategories.map((c) => [c.category, c.count])),
            }))}
          />
        </Figure>
      )}
    </>
  );
}

function sumCats(p: RosterPerson): number {
  return p.workCategories.reduce((s, c) => s + c.count, 0);
}

function capital(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function Definitions() {
  return (
    <>
      <div className={ed.sect}>How these numbers are counted</div>
      <dl className={ed.defs}>
        <div>
          <dt>Who is listed</dt>
          <dd>
            Everyone who opened, merged or reviewed a PR, authored a commit, or delivered a JIRA
            ticket in the period. No minimum, no cap. Bots and coding agents are not people; an
            agent's commits are credited to whoever opened the PR.
          </dd>
        </div>
        <div>
          <dt>Merged</dt>
          <dd>
            PRs the person authored that merged inside the window, counted on the merge date. Promotion PRs
            between long-lived branches (staging to main, develop to staging) don't count for anyone; the
            work in them is counted in the PRs that brought it in.
          </dd>
        </div>
        <div>
          <dt>Reviewed</dt>
          <dd>
            Distinct PRs by someone else that the person reviewed in the window. Replies on their own
            PR don't count; nor do repeat comments on the same PR, or approvals of promotion PRs.
          </dd>
        </div>
        <div>
          <dt>Commits</dt>
          <dd>
            Authored commits only: no merge commits, and no squash commit when the PR's branch commits
            are already counted.
          </dd>
        </div>
        <div>
          <dt>Tickets</dt>
          <dd>
            JIRA issues in a done status resolved in the window (last update if the board sets no
            resolution date), plus issues that moved into code review or QA in it. Credited to the
            current assignee; reassignment history isn't synced.
          </dd>
        </div>
        <div>
          <dt>Periods</dt>
          <dd>
            The last 30, 60 or 90 days, today included, against the same number of days just
            before.
          </dd>
        </div>
        <div>
          <dt>Score</dt>
          <dd>
            0 to 100: delivery (40), durability (35) and reviews of other people's work (25), each against the 90th
            percentile of the org. Durability is earned in proportion to delivery, so a quiet period with nothing to break
            doesn't outrank a busy one. Commits and story points are shown, not scored.
          </dd>
        </div>
        <div>
          <dt>Team medians</dt>
          <dd>Over people who did code work in the period, so idle and ticket-only profiles don't pull them down.</dd>
        </div>
      </dl>
    </>
  );
}
